// Reading and writing a Modbus connection as code.
//
// The wizard's builder (edge-connector-spec.js) is the one place a signed
// connector spec is constructed, and this module is the round trip around it:
// a spec turned back into the form draft the wizard was filled in with, a
// pasted document turned into a validated spec, and a spec turned into a
// reusable device template with the three device-specific fields removed.
//
// Nothing here is a second builder. Every path ends at
// buildEdgeModbusConnectorSpec, so a connection authored as code is validated
// by exactly the rules a connection authored in the form is — and a pasted
// document that the form could not have produced is rejected here rather than
// three network hops later by the control plane.

import { buildEdgeModbusConnectorSpec } from "./edge-connector-spec";

const TRANSPORT_KINDS = {
  tcp: "modbus-tcp",
  rtu: "modbus-rtu",
  rtu_over_tcp: "modbus-rtu-over-tcp",
};

const DATA_TYPE_TO_TAG_TYPE = {
  bool: "boolean",
  int64: "integer",
  float64: "number",
  string: "string",
};

const TAG_TYPE_TO_WIRE = { boolean: "bool", integer: "int64", number: "float64", string: "string" };

// A path-prefixed rejection. The operator is looking at a JSON document, so
// "mappings[3].native_config.address" locates the mistake in what is on screen;
// "Point 4" locates it in a form they are not looking at.
class SpecCodeError extends Error {
  constructor(path, message) {
    super(path ? `${path}: ${message}` : message);
    this.path = path;
  }
}

const isObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const requireObjectAt = (value, path) => {
  if (!isObject(value)) throw new SpecCodeError(path, "expected an object.");
  return value;
};

const requireTextAt = (value, path) => {
  if (typeof value !== "string" || !value.trim()) {
    throw new SpecCodeError(path, "expected a non-empty string.");
  }
  return value.trim();
};

const requireIntegerAt = (value, path) => {
  const number = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  if (!Number.isSafeInteger(number)) throw new SpecCodeError(path, "expected a whole number.");
  return number;
};

export const formatEdgeModbusSpec = (spec) => `${JSON.stringify(spec, null, 2)}\n`;

// The form kind a spec's transport belongs to. Modbus is one stored kind with
// three form kinds because the form asks different questions of a serial bus.
export const edgeModbusSpecKind = (spec) => {
  const transport = String(spec?.config?.transport || "tcp").trim().toLowerCase();
  return TRANSPORT_KINDS[transport] || "";
};

const specTagFolder = (tagPath) => {
  const segments = String(tagPath || "").split("/").filter(Boolean);
  // default/<folder…>/<name>
  return segments.slice(1, -1).join("/");
};

const specTagName = (tagPath) => {
  const segments = String(tagPath || "").split("/").filter(Boolean);
  return segments[segments.length - 1] || "";
};

// edgeModbusSpecToDraft turns a spec back into the draft EdgeModbusDeviceForm
// opens with.
//
// Every point comes back as a tag to CREATE rather than an existing one to
// pick. A pasted spec makes no claim about what already exists on this
// workspace, and the connect path already treats an identical existing tag as
// success (preparePendingConnector re-resolves it and accepts a byte-for-byte
// match), so this is the reading that is right whether the tags are there or
// not — and a tag that exists with a different type stays a visible conflict
// instead of being silently reused.
export const edgeModbusSpecToDraft = (spec, { expectedRevision } = {}) => {
  const config = spec?.config || {};
  const transport = String(config.transport || "tcp").trim().toLowerCase();
  const asString = (value) => (value == null ? "" : String(value));
  return {
    connector_id: asString(spec?.id),
    ...(Number.isSafeInteger(expectedRevision) ? { expected_revision: expectedRevision } : {}),
    desired_state: asString(spec?.desired_state || "running"),
    name: asString(spec?.name),
    settings: {
      // The Turck shortcut is a property of the form, not of a connection: a
      // spec carries every value it produced, so code always opens generic.
      deviceTemplate: "generic",
      host: asString(config.tcp?.host),
      port: asString(config.tcp?.port ?? 502),
      serialPort: asString(config.rtu?.port),
      baudRate: asString(config.rtu?.baud_rate ?? 19200),
      dataBits: asString(config.rtu?.data_bits ?? 8),
      stopBits: asString(config.rtu?.stop_bits ?? 1),
      parity: asString(config.rtu?.parity || "even"),
      unitId: asString(config.unit_id ?? (transport === "tcp" ? 1 : 1)),
      timeoutMs: asString(config.timeout_ms ?? 2000),
      reconnectMinMs: asString(config.reconnect_min_ms ?? 250),
      reconnectMaxMs: asString(config.reconnect_max_ms ?? 30000),
    },
    points: (Array.isArray(spec?.mappings) ? spec.mappings : []).map((mapping) => {
      const native = mapping?.native_config || {};
      const tagType = DATA_TYPE_TO_TAG_TYPE[String(mapping?.data_type || "")] || "number";
      return {
        tag_mode: "new",
        tag_path: asString(mapping?.tag_path),
        new_tag_folder: specTagFolder(mapping?.tag_path),
        new_tag_name: specTagName(mapping?.tag_path),
        new_tag_type: tagType,
        settings: {
          function: native.function == null ? "" : String(native.function),
          address: asString(native.address ?? 0),
          // A spec always carries the protocol address; the one-based option is
          // a data-entry convenience of the form and never reaches the wire.
          addressBase: "zero_based",
          valueType: tagType === "boolean" ? "" : asString(native.value_type),
          bit: asString(native.bit ?? 0),
          byteOrder: asString(native.byte_order || "big"),
          wordOrder: asString(native.word_order || "big"),
          scale: asString(native.scale ?? 1),
          offset: asString(native.offset ?? 0),
          pollIntervalMs: asString(native.poll_interval_ms ?? 100),
          maxReadGap: asString(native.max_read_gap ?? 0),
          maxReadCount: asString(native.max_read_count ?? ""),
          failurePolicy: asString(native.failure_policy || "last_good"),
          writeConfirmation: asString(native.write_confirmation || "readback"),
          readWindowMode: native.read_window_start != null || native.read_window_count != null ? "fixed" : "automatic",
          readWindowStart: asString(native.read_window_start ?? native.address ?? 0),
          readWindowCount: asString(native.read_window_count ?? 1),
          stringLength: asString(native.string_length ?? 32),
          stringRaw: Boolean(native.string_raw),
          stringRightJustify: Boolean(native.string_right_justify),
          alias: asString(native.alias),
          // Flow is an authority boundary: preserve exactly what the document
          // says so re-saving a monitoring connection cannot turn it into one
          // that writes.
          flow: asString(mapping?.flow),
        },
      };
    }),
  };
};

// The wizard points a draft's points map onto, ready for the builder.
const draftPoints = (draft) => draft.points.map((point) => ({
  tagPath: point.tag_path,
  tagType: point.new_tag_type,
  direction: ["command", "bidirectional"].includes(point.settings.flow) ? "output" : "input",
  settings: point.settings,
}));

// parseEdgeModbusSpecCode validates a pasted document and answers the canonical
// spec the builder produces from it — which is what /connectors/preview would
// have received. Structural problems are reported by JSON path; everything
// after that is the wizard's own point-numbered rules.
export function parseEdgeModbusSpecCode(text) {
  let parsed;
  try {
    parsed = JSON.parse(String(text || ""));
  } catch (error) {
    throw new SpecCodeError("", `This is not valid JSON — ${String(error?.message || error)}`);
  }
  requireObjectAt(parsed, "");
  requireTextAt(parsed.name, "name");
  if (parsed.kind != null && String(parsed.kind) !== "modbus") {
    throw new SpecCodeError("kind", 'only "modbus" connections can be edited as code here.');
  }
  if (parsed.desired_state != null && !["running", "stopped"].includes(String(parsed.desired_state))) {
    throw new SpecCodeError("desired_state", 'expected "running" or "stopped".');
  }
  const config = requireObjectAt(parsed.config, "config");
  const kind = edgeModbusSpecKind(parsed);
  if (!kind) {
    throw new SpecCodeError("config.transport", 'expected "tcp", "rtu", or "rtu_over_tcp".');
  }
  requireIntegerAt(config.unit_id ?? 1, "config.unit_id");
  if (kind === "modbus-rtu") {
    requireObjectAt(config.rtu, "config.rtu");
    requireTextAt(config.rtu.port, "config.rtu.port");
  } else {
    requireObjectAt(config.tcp, "config.tcp");
    requireTextAt(config.tcp.host, "config.tcp.host");
    requireIntegerAt(config.tcp.port ?? 502, "config.tcp.port");
  }
  if (!Array.isArray(parsed.mappings) || parsed.mappings.length === 0) {
    throw new SpecCodeError("mappings", "expected at least one mapping.");
  }
  parsed.mappings.forEach((mapping, index) => {
    const at = `mappings[${index}]`;
    requireObjectAt(mapping, at);
    const tagPath = requireTextAt(mapping.tag_path, `${at}.tag_path`);
    if (!tagPath.startsWith("default/") || tagPath.split("/").filter(Boolean).length < 2) {
      throw new SpecCodeError(`${at}.tag_path`, 'expected a project-rooted path such as "default/solis/SOC".');
    }
    const dataType = requireTextAt(mapping.data_type, `${at}.data_type`);
    if (!DATA_TYPE_TO_TAG_TYPE[dataType]) {
      throw new SpecCodeError(`${at}.data_type`, 'expected "bool", "int64", "float64", or "string".');
    }
    if (mapping.flow != null && !["observe", "command", "bidirectional"].includes(String(mapping.flow))) {
      throw new SpecCodeError(`${at}.flow`, 'expected "observe", "command", or "bidirectional".');
    }
    const native = requireObjectAt(mapping.native_config, `${at}.native_config`);
    requireIntegerAt(native.function, `${at}.native_config.function`);
    requireIntegerAt(native.address, `${at}.native_config.address`);
  });

  const draft = edgeModbusSpecToDraft(parsed);
  let spec;
  try {
    spec = buildEdgeModbusConnectorSpec({
      kind,
      name: draft.name,
      connectorID: draft.connector_id,
      desiredState: draft.desired_state,
      settings: draft.settings,
      points: draftPoints(draft),
    });
  } catch (buildError) {
    // The builder numbers its points from 1; the document indexes from 0. Say
    // both so the operator can find the mapping in the text they are editing.
    const message = String(buildError?.message || buildError);
    const numbered = /^Point (\d+): (.*)$/.exec(message);
    if (numbered) throw new SpecCodeError(`mappings[${Number(numbered[1]) - 1}]`, numbered[2]);
    throw new SpecCodeError("", message);
  }
  return { spec, kind, draft };
}

// The cloud tags a spec's mappings need, in the shape preparePendingConnector
// creates and deploys. Derived from the spec so a tag's type and writability
// cannot drift from the mapping that binds it.
export const edgeModbusPendingTagsFromSpec = (spec, deviceName = "") =>
  (Array.isArray(spec?.mappings) ? spec.mappings : []).map((mapping) => {
    const fullPath = String(mapping?.tag_path || "");
    const segments = fullPath.split("/").filter(Boolean);
    const writable = ["command", "bidirectional"].includes(String(mapping?.flow || ""));
    return {
      fullPath,
      // POST /api/tags takes the path under the project root; the runtime and
      // the mapping both speak the full "default/…" path.
      path: segments.slice(1).join("/"),
      name: segments[segments.length - 1] || "",
      type: String(mapping?.data_type || "float64"),
      writable,
      description: deviceName ? `Created for ${deviceName}` : "",
      direction: writable ? "output" : "input",
    };
  });

// edgeModbusSpecFromConnector reassembles the spec a listed connection is
// running, from the redacted settings view the server publishes
// (core/scada/edgerelayserver/server_connector_settings.go). That view exists
// precisely so a connection can be read back in full — it reports
// `editable: false` for any kind it cannot — which makes it the honest source
// for "save this connection as a template" and "export this connection as
// code". Returns null when the connection is not a readable Modbus client.
export function edgeModbusSpecFromConnector(connector) {
  const device = connector?.settings?.modbus;
  const points = Array.isArray(connector?.settings?.points) ? connector.settings.points : [];
  if (!connector?.settings?.editable || !device || !points.length) return null;
  const transport = String(device.transport || "tcp").trim().toLowerCase();
  return {
    id: String(connector.connector_id || ""),
    name: String(connector.name || ""),
    desired_state: String(connector.desired_state || "running"),
    kind: "modbus",
    config: {
      transport,
      unit_id: Number(device.unit_id || 0),
      timeout_ms: Number(device.timeout_ms || 2000),
      reconnect_min_ms: Number(device.reconnect_min_ms || 250),
      reconnect_max_ms: Number(device.reconnect_max_ms || 30000),
      ...(transport === "rtu" ? {
        rtu: {
          port: String(device.serial_port || ""),
          baud_rate: Number(device.baud_rate || 19200),
          data_bits: Number(device.data_bits || 8),
          stop_bits: Number(device.stop_bits || 1),
          parity: String(device.parity || "even"),
        },
      } : {
        tcp: { host: String(device.host || ""), port: Number(device.port || 502) },
      }),
    },
    mappings: points.map((point) => ({
      tag_path: String(point.tag_path || ""),
      native_config: point.modbus || {},
      flow: String(point.flow || "observe"),
      data_type: String(point.data_type || "float64"),
    })),
  };
}

// The poll interval most of a device's points share becomes the template's
// default, so only the points that deliberately differ (a control register read
// every 2 s) carry one of their own.
const dominantPollInterval = (mappings) => {
  const counts = new Map();
  mappings.forEach((mapping) => {
    const poll = Number(mapping?.native_config?.poll_interval_ms);
    if (!Number.isSafeInteger(poll)) return;
    counts.set(poll, (counts.get(poll) || 0) + 1);
  });
  let best = 0;
  let bestCount = 0;
  counts.forEach((count, poll) => {
    if (count > bestCount || (count === bestCount && poll < best)) {
      best = poll;
      bestCount = count;
    }
  });
  return best || 1000;
};

// edgeModbusTemplateFromSpec strips a connection down to what is reusable.
//
// Host, port-of-this-device and unit id come out: they are exactly what differs
// between two devices of the same model, and a template that carried them would
// only ever describe the one device it was saved from. The TCP port survives as
// `default_port` because a model has a standard port even though a device may
// not be on it.
export function edgeModbusTemplateFromSpec(spec, { name, vendor = "", model = "", description = "" } = {}) {
  const kind = edgeModbusSpecKind(spec);
  if (kind !== "modbus-tcp" && kind !== "modbus-rtu") {
    throw new Error("Only Modbus TCP and Modbus RTU connections can be saved as a device template.");
  }
  const mappings = Array.isArray(spec?.mappings) ? spec.mappings : [];
  if (!mappings.length) throw new Error("This connection has no points to save.");
  const config = spec?.config || {};
  const defaultPoll = dominantPollInterval(mappings);

  const folders = new Set(mappings.map((mapping) => specTagFolder(mapping.tag_path)));
  const tagFolderDefault = folders.size === 1 ? [...folders][0] : "";

  const points = mappings.map((mapping) => {
    const native = mapping?.native_config || {};
    const tagType = DATA_TYPE_TO_TAG_TYPE[String(mapping?.data_type || "")] || "number";
    const writable = ["command", "bidirectional"].includes(String(mapping?.flow || ""));
    const poll = Number(native.poll_interval_ms);
    return {
      name: specTagName(mapping.tag_path),
      address: Number(native.address || 0),
      function: Number(native.function || 0),
      tag_type: tagType,
      ...(tagType === "boolean" ? {} : { value_type: String(native.value_type || "") }),
      ...(tagType === "number" && native.scale != null ? { scale: Number(native.scale) } : {}),
      ...(tagType === "number" && native.offset != null ? { offset: Number(native.offset) } : {}),
      ...(writable ? { writable: true } : {}),
      ...(native.bit == null ? {} : { bit: Number(native.bit) }),
      ...(Number.isSafeInteger(poll) && poll !== defaultPoll ? { poll_interval_ms: poll } : {}),
    };
  });

  return {
    name: String(name || "").trim(),
    vendor: String(vendor || "").trim(),
    model: String(model || "").trim(),
    kind,
    description: String(description || "").trim(),
    tag_folder_default: tagFolderDefault,
    settings: {
      timeout_ms: Number(config.timeout_ms || 2000),
      reconnect_min_ms: Number(config.reconnect_min_ms || 250),
      reconnect_max_ms: Number(config.reconnect_max_ms || 30000),
      poll_interval_ms: defaultPoll,
      ...(kind === "modbus-tcp" ? { default_port: Number(config.tcp?.port || 502) } : {
        baud_rate: Number(config.rtu?.baud_rate || 19200),
        data_bits: Number(config.rtu?.data_bits || 8),
        stop_bits: Number(config.rtu?.stop_bits || 1),
        parity: String(config.rtu?.parity || "even"),
      }),
    },
    points,
  };
}

export { SpecCodeError, DATA_TYPE_TO_TAG_TYPE, TAG_TYPE_TO_WIRE };
